import { PinyinKeyboardKey } from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-keyboard.__components.keyboard.__components.key";
import {
  getPinyinKeyboardKeyHeight,
  getPinyinKeyboardKeyWidth,
  KEY_GAP,
  PINYIN_KEYBOARD_HORIZONTAL_GUTTER,
  PINYIN_KEYBOARD_ROW_LIST,
  type PinyinKey,
} from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-keyboard._layout";
import { cn } from "@-/com.npmjs.cn";
import { useCallback, useImperativeHandle, useMemo, useRef, useState, type Ref } from "react";
import type { GestureResponderEvent, LayoutChangeEvent } from "@-/com.npmjs.react-native";
import { View } from "@-/com.npmjs.react-native";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { getLetterKey, interpretKeystrokes } from "#pinyin-keyboard-keystroke-interpretation";

export interface PinyinKeyboardKeysHandle {
  pressQwertyKey: (qwertyKey: string) => void;
}

/** First tone is on the `;` key (next to L); bottom row only has tones 2–4. */
const FIRST_TONE_MARK = "\u02C9"; // MODIFIER LETTER MACRON — same glyph as former tone-1 button

/**
 * When `false`, the first-tone cell keeps its slot but only shows the flat-tone glyph (like other tone keys).
 * When `true`, it shows the small hint above and `;` bottom-left like a letter key.
 */
const SHOW_SEMICOLON_QWERTY_LABEL = false;

/** Trailing tone digits for bottom-row keys (tones 2–4); tone 1 uses `;`. */
const TONE_BUTTON_LIST = [
  { digit: "2" as const, mark: "\u02CA" },
  { digit: "3" as const, mark: "\u02C7" },
  { digit: "4" as const, mark: "\u02CB" },
] as const;

/** Neutral tone (tone 0) marker — dot above, shown on the `tone0` key (top-right). */
const NEUTRAL_TONE_MARK = "\u02D9"; // DOT ABOVE (˙)

type ToneDigit = "0" | "1" | "2" | "3" | "4";

const returnFalse = () => false;

const TRAILING_TONE_DIGIT = /([0-5])$/;
const TONE_DIGIT_SUFFIX = /[0-5]$/;
const OPTIONAL_TONE_DIGIT_SUFFIX = /[0-5]?$/;

function getVisiblePinyinList(key: PinyinKey): readonly string[] {
  return [
    ...("initial" in key && key.initial !== key.qwertyKey ? [key.initial] : []),
    ...key.finalList,
  ];
}

/** The letter keys tapped for the current syllable, and the field text they produced. */
type KeystrokeBuffer = { keystrokes: string; output: string };

const EMPTY_KEYSTROKE_BUFFER: KeystrokeBuffer = { keystrokes: "", output: "" };

/** `nü` and `nv` are the same field text: the preview arrives in tone-number form (`v`). */
function toComparableFieldText(text: string): string {
  return text.trim().toLowerCase().replaceAll("ü", "v");
}

/** A lone initial key is highlighted until the next tap completes the syllable. */
function getPendingInitial(keystrokes: string): string | null {
  if (keystrokes.length !== 1) {
    return null;
  }
  const key = getLetterKey(keystrokes);
  return key && "initial" in key ? key.initial : null;
}

export function PinyinKeyboardKeys({
  backspaceBehavior = "deleteLastCharacter",
  className,
  disabled = false,
  hidePinyinHints = false,
  hideToneKeys = false,
  innerWidth,
  onLayout,
  onPendingInitialChange,
  onPinyinSelect,
  onTonePressed,
  pinyinPreview,
  ref,
  shuangpinEnabled = true,
  swipeEnabled = false,
  validPinyinSet,
}: {
  backspaceBehavior?: "deleteLastCharacter" | "clearInput";
  className?: string;
  disabled?: boolean;
  /** When true, suppress the small pinyin hint labels on letter keys (qwerty mode). */
  hidePinyinHints?: boolean;
  hideToneKeys?: boolean;
  /** Pre-computed inner width of the keys area (windowWidth minus horizontal gutters on both sides). */
  innerWidth: number;
  /** Fired with the laid-out size of the keys grid (used to drive the dismiss slide/collapse). */
  onLayout?: (event: LayoutChangeEvent) => void;
  onPendingInitialChange?: (initial: string | null) => void;
  onPinyinSelect: (pinyin: string) => void;
  /** Fired after a tone is applied to the pinyin preview, with the resulting preview value. */
  onTonePressed?: (pinyin: string) => void;
  /** When set, tones 2–4 appear after M; tone 1 is the `;` key next to L. */
  pinyinPreview?: string;
  ref?: Ref<PinyinKeyboardKeysHandle>;
  /**
   * When true (the default), two taps are read as one Xiaohe shuangpin syllable; a third tap
   * switches back to letter-by-letter. When false, every tap is taken literally.
   */
  shuangpinEnabled?: boolean;
  /** When true, swiping from an initial key to a final key commits initial+final in one stroke. */
  swipeEnabled?: boolean;
  validPinyinSet?: ReadonlySet<string>;
}) {
  const [bufferedKeystrokes, setBufferedKeystrokes] = useRouteBodyState(
    "pinyin-keyboard-keystrokes",
    "",
  );
  const [bufferedOutput, setBufferedOutput] = useRouteBodyState(
    "pinyin-keyboard-keystroke-output",
    "",
  );
  const setKeystrokeBuffer = useCallback(
    ({ keystrokes, output }: KeystrokeBuffer) => {
      setBufferedKeystrokes(keystrokes);
      setBufferedOutput(output);
    },
    [setBufferedKeystrokes, setBufferedOutput],
  );
  // The field also changes without these keys (a tone, the OS keyboard, a checked answer). Taps only
  // continue the syllable while the field still shows what they produced.
  const activeKeystrokes =
    pinyinPreview === undefined ||
    toComparableFieldText(pinyinPreview) === toComparableFieldText(bufferedOutput)
      ? bufferedKeystrokes
      : "";
  const pendingInitial = getPendingInitial(activeKeystrokes);
  const [swipeHoverQwertyKey, setSwipeHoverQwertyKey] = useState<string | null>(null);
  const swipeStateRef = useRef<{ startQwertyKey: string } | null>(null);
  const swipeStartRef = useRef<{ startX: number; startY: number } | null>(null);

  const showToneKeys = !hideToneKeys && pinyinPreview !== undefined;

  const keyWidth = useMemo(
    () =>
      getPinyinKeyboardKeyWidth(innerWidth + PINYIN_KEYBOARD_HORIZONTAL_GUTTER * 2, showToneKeys),
    [innerWidth, showToneKeys],
  );

  const keyHeight = getPinyinKeyboardKeyHeight(keyWidth);

  const halfKeyStagger = useMemo(() => (keyWidth + KEY_GAP) / 2, [keyWidth]);

  const hasPinyinForTones = (pinyinPreview ?? "").trim().length > 0;

  const handlePressTone = useCallback(
    (toneDigit: ToneDigit) => {
      if (disabled || !hasPinyinForTones || pinyinPreview === undefined) {
        return;
      }
      const trailingMatch = pinyinPreview.match(TRAILING_TONE_DIGIT);
      const currentDigit = trailingMatch?.[1];
      const nextPinyin =
        currentDigit === toneDigit
          ? pinyinPreview.replace(TONE_DIGIT_SUFFIX, "")
          : pinyinPreview.replace(OPTIONAL_TONE_DIGIT_SUFFIX, toneDigit);
      onPinyinSelect(nextPinyin);
      onTonePressed?.(nextPinyin);
    },
    [disabled, hasPinyinForTones, onPinyinSelect, onTonePressed, pinyinPreview],
  );

  const commitKeystrokes = useCallback(
    (keystrokes: string) => {
      onPendingInitialChange?.(getPendingInitial(keystrokes));
      if (keystrokes.length === 0) {
        setKeystrokeBuffer(EMPTY_KEYSTROKE_BUFFER);
        onPinyinSelect("");
        return;
      }
      const output =
        interpretKeystrokes({ keystrokes, shuangpinEnabled, validPinyinSet }) ?? keystrokes;
      setKeystrokeBuffer({ keystrokes, output });
      onPinyinSelect(output);
    },
    [onPendingInitialChange, onPinyinSelect, setKeystrokeBuffer, shuangpinEnabled, validPinyinSet],
  );

  const handleBackspace = useCallback(() => {
    if (disabled) {
      return;
    }
    if (backspaceBehavior === "clearInput") {
      setKeystrokeBuffer(EMPTY_KEYSTROKE_BUFFER);
      onPendingInitialChange?.(null);
      if (pinyinPreview) {
        // First press after a tone has been marked strips just the tone digit (so the user can
        // re-pick a tone without retyping the syllable); the next press clears the whole word.
        if (TONE_DIGIT_SUFFIX.test(pinyinPreview)) {
          onPinyinSelect(pinyinPreview.replace(TONE_DIGIT_SUFFIX, ""));
          return;
        }
        onPinyinSelect("");
      }
      return;
    }
    if (pinyinPreview && TONE_DIGIT_SUFFIX.test(pinyinPreview)) {
      onPinyinSelect(pinyinPreview.replace(TONE_DIGIT_SUFFIX, ""));
      return;
    }
    // Undo the last tap, re-reading what's left (`zho` → `zh`, shown as shuangpin `zang`).
    if (activeKeystrokes.length > 0) {
      commitKeystrokes(activeKeystrokes.slice(0, -1));
      return;
    }
    if (pinyinPreview) {
      onPinyinSelect(pinyinPreview.slice(0, -1));
    }
  }, [
    activeKeystrokes,
    backspaceBehavior,
    commitKeystrokes,
    disabled,
    onPendingInitialChange,
    onPinyinSelect,
    pinyinPreview,
    setKeystrokeBuffer,
  ]);

  const handleKeyPress = useCallback(
    (key: PinyinKey) => {
      if (disabled) {
        return;
      }

      if (key.qwertyKey === ";") {
        handlePressTone("1");
        return;
      }

      if (key.qwertyKey === "tone0") {
        handlePressTone("0");
        return;
      }

      // A tap that can't continue the current syllable starts a new one.
      const continued = `${activeKeystrokes}${key.qwertyKey}`;
      commitKeystrokes(
        interpretKeystrokes({ keystrokes: continued, shuangpinEnabled, validPinyinSet }) === null
          ? key.qwertyKey
          : continued,
      );
    },
    [
      activeKeystrokes,
      commitKeystrokes,
      disabled,
      handlePressTone,
      shuangpinEnabled,
      validPinyinSet,
    ],
  );

  const hitTest = useCallback(
    (locationX: number, locationY: number): PinyinKey | null => {
      if (keyWidth <= 0) {
        return null;
      }
      const rowStride = keyHeight + KEY_GAP;
      const rowIndex = Math.floor(locationY / rowStride);
      if (rowIndex < 0 || rowIndex >= arrayLength(PINYIN_KEYBOARD_ROW_LIST)) {
        return null;
      }
      const rowTop = rowIndex * rowStride;
      if (locationY > rowTop + keyHeight) {
        return null;
      }
      const rowPaddingLeft = rowIndex * halfKeyStagger;
      const xInRow = locationX - rowPaddingLeft;
      if (xInRow < 0) {
        return null;
      }
      const cellStride = keyWidth + KEY_GAP;
      const colIndex = Math.floor(xInRow / cellStride);
      const row = PINYIN_KEYBOARD_ROW_LIST[rowIndex];
      if (colIndex < 0 || colIndex >= arrayLength(row)) {
        return null;
      }
      const cellLeft = colIndex * cellStride;
      if (xInRow > cellLeft + keyWidth) {
        return null;
      }
      const key = row[colIndex];
      return !showToneKeys && (key.qwertyKey === "tone0" || key.qwertyKey === ";") ? null : key;
    },
    [halfKeyStagger, keyHeight, keyWidth, showToneKeys],
  );

  const endSwipe = useCallback(
    (commitKey: PinyinKey | null) => {
      const swipe = swipeStateRef.current;
      swipeStateRef.current = null;
      setSwipeHoverQwertyKey(null);
      if (!swipe) {
        return;
      }
      if (
        !commitKey ||
        commitKey.qwertyKey === swipe.startQwertyKey ||
        commitKey.qwertyKey === "tone0" ||
        commitKey.qwertyKey === ";"
      ) {
        // No drag or invalid destination — leave the initial pending so a follow-up tap finishes it.
        return;
      }
      const keystrokes = `${swipe.startQwertyKey}${commitKey.qwertyKey}`;
      if (interpretKeystrokes({ keystrokes, shuangpinEnabled, validPinyinSet }) !== null) {
        commitKeystrokes(keystrokes);
      }
    },
    [commitKeystrokes, shuangpinEnabled, validPinyinSet],
  );

  const isSwipeActive = swipeEnabled && !disabled;
  const SWIPE_THRESHOLD_PX = 8;

  const handleStartShouldSetResponderCapture = useCallback(
    (event: GestureResponderEvent) => {
      if (!isSwipeActive) {
        return false;
      }
      const { locationX, locationY } = event.nativeEvent;
      swipeStartRef.current = { startX: locationX, startY: locationY };
      // Let the touch fall through to the child Pressable until the user actually drags.
      return false;
    },
    [isSwipeActive],
  );

  const handleMoveShouldSetResponderCapture = useCallback(
    (event: GestureResponderEvent) => {
      if (!isSwipeActive || !swipeStartRef.current) {
        return false;
      }
      const { locationX, locationY } = event.nativeEvent;
      const dx = locationX - swipeStartRef.current.startX;
      const dy = locationY - swipeStartRef.current.startY;
      if (dx * dx + dy * dy < SWIPE_THRESHOLD_PX * SWIPE_THRESHOLD_PX) {
        return false;
      }
      const startKey = hitTest(swipeStartRef.current.startX, swipeStartRef.current.startY);
      if (!startKey || !("initial" in startKey)) {
        return false;
      }
      // A swipe is a whole two-key syllable, so it always starts a new one.
      swipeStateRef.current = { startQwertyKey: startKey.qwertyKey };
      commitKeystrokes(startKey.qwertyKey);
      const currentKey = hitTest(locationX, locationY);
      setSwipeHoverQwertyKey(currentKey ? currentKey.qwertyKey : startKey.qwertyKey);
      return true;
    },
    [commitKeystrokes, hitTest, isSwipeActive],
  );

  const handleResponderMove = useCallback(
    (event: GestureResponderEvent) => {
      if (!swipeStateRef.current) {
        return;
      }
      const { locationX, locationY } = event.nativeEvent;
      const key = hitTest(locationX, locationY);
      setSwipeHoverQwertyKey(key ? key.qwertyKey : null);
    },
    [hitTest],
  );

  const handleResponderRelease = useCallback(
    (event: GestureResponderEvent) => {
      swipeStartRef.current = null;
      if (!swipeStateRef.current) {
        return;
      }
      const { locationX, locationY } = event.nativeEvent;
      const key = hitTest(locationX, locationY);
      endSwipe(key);
    },
    [endSwipe, hitTest],
  );

  const handleResponderTerminate = useCallback(() => {
    swipeStartRef.current = null;
    swipeStateRef.current = null;
    setSwipeHoverQwertyKey(null);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      pressQwertyKey(qwertyKey: string) {
        // `0` and `5` both mark the neutral tone (canonicalized to `0`).
        if (qwertyKey === "0" || qwertyKey === "5") {
          handlePressTone("0");
          return;
        }
        if (qwertyKey === "1" || qwertyKey === "2" || qwertyKey === "3" || qwertyKey === "4") {
          handlePressTone(qwertyKey);
          return;
        }
        // Backspace is only rendered while the tone keys are hidden, so it also stays reachable
        // programmatically (e.g. a web hardware keyboard) with the same `backspaceBehavior`.
        if (qwertyKey === "backspace") {
          handleBackspace();
          return;
        }
        for (const row of PINYIN_KEYBOARD_ROW_LIST) {
          for (const key of row) {
            if (key.qwertyKey === qwertyKey) {
              handleKeyPress(key);
              return;
            }
          }
        }
      },
    }),
    [handleBackspace, handleKeyPress, handlePressTone],
  );

  return (
    <View
      className={cn("relative gap-1", disabled && "opacity-50", className)}
      onLayout={onLayout}
      onMoveShouldSetResponderCapture={handleMoveShouldSetResponderCapture}
      onResponderMove={handleResponderMove}
      onResponderRelease={handleResponderRelease}
      onResponderTerminate={handleResponderTerminate}
      onResponderTerminationRequest={returnFalse}
      onStartShouldSetResponderCapture={handleStartShouldSetResponderCapture}
    >
      {PINYIN_KEYBOARD_ROW_LIST.map((row, rowIndex) => (
        <View
          className="flex-row gap-1"
          key={rowIndex}
          style={{
            paddingLeft: rowIndex * halfKeyStagger,
          }}
        >
          {row
            .filter((key) => showToneKeys || (key.qwertyKey !== "tone0" && key.qwertyKey !== ";"))
            .map((key) => {
              if (key.qwertyKey === "tone0") {
                return (
                  <PinyinKeyboardKey
                    accessibilityLabel="Neutral tone (tone 0); press again to clear tone digit"
                    digitLabel="0"
                    digitLabelRight="5"
                    dimmed={disabled || !hasPinyinForTones}
                    disabled={disabled || !hasPinyinForTones}
                    key="tone0"
                    keyHeight={keyHeight}
                    keyWidth={keyWidth}
                    layout="tone"
                    onPress={() => handleKeyPress(key)}
                    toneMark={NEUTRAL_TONE_MARK}
                  />
                );
              }

              const isInitialPending =
                key.qwertyKey !== ";" && "initial" in key && pendingInitial === key.initial;
              const isSwipeHover = swipeHoverQwertyKey === key.qwertyKey;
              const isPending = isInitialPending || isSwipeHover;
              const pinyinList = hidePinyinHints ? [] : getVisiblePinyinList(key);

              if (!SHOW_SEMICOLON_QWERTY_LABEL && key.qwertyKey === ";") {
                return (
                  <PinyinKeyboardKey
                    accessibilityLabel="First tone (flat); press again to clear tone digit"
                    digitLabel="1"
                    dimmed={disabled || !hasPinyinForTones}
                    disabled={disabled}
                    key=";"
                    keyHeight={keyHeight}
                    keyWidth={keyWidth}
                    layout="tone"
                    onPress={() => handleKeyPress(key)}
                    toneMark={FIRST_TONE_MARK}
                  />
                );
              }

              return (
                <PinyinKeyboardKey
                  accessibilityLabel={`${key.qwertyKey.toUpperCase()} ${pinyinList.join(" ")}`}
                  disabled={disabled}
                  isPending={isPending}
                  key={key.qwertyKey}
                  keyHeight={keyHeight}
                  keyWidth={keyWidth}
                  layout="letter"
                  onPress={() => handleKeyPress(key)}
                  pinyinLabelList={pinyinList}
                  qwertyKey={key.qwertyKey}
                />
              );
            })}
          {showToneKeys &&
            rowIndex === arrayLength(PINYIN_KEYBOARD_ROW_LIST) - 1 &&
            TONE_BUTTON_LIST.map(({ digit, mark }) => (
              <PinyinKeyboardKey
                accessibilityLabel={`Tone ${digit}, press again to clear`}
                digitLabel={digit}
                dimmed={disabled || !hasPinyinForTones}
                disabled={disabled || !hasPinyinForTones}
                key={`tone-${digit}`}
                keyHeight={keyHeight}
                keyWidth={keyWidth}
                layout="tone"
                onPress={() => handlePressTone(digit)}
                toneMark={mark}
              />
            ))}
          {/* Without the trailing tone keys, the bottom row's free right end holds a double-width
              backspace, leaving the staggered bottom-left slot for the host's toggle key. */}
          {!showToneKeys && rowIndex === arrayLength(PINYIN_KEYBOARD_ROW_LIST) - 1 ? (
            <PinyinKeyboardKey
              accessibilityLabel="Backspace"
              disabled={disabled}
              iconName="MdBackspace"
              key="backspace"
              keyHeight={keyHeight}
              keyWidth={keyWidth * 2 + KEY_GAP}
              layout="icon"
              onPress={handleBackspace}
            />
          ) : null}
        </View>
      ))}
    </View>
  );
}
